import { useCallback } from 'react';
import { useLazyQuery, useMutation, ApolloError, LazyQueryExecFunction, OperationVariables } from '@apollo/client';
import {
  GET_TALENT_BOARD_EVENTS,
  GET_TALENT_BOARD_EVENT_KPIS,
  CREATE_TALENT_BOARD_EVENT,
} from '@bloomays-lib/adapter.api-talent';
import {
  GqlTalentBoardEvent,
  GqlTalentBoardEventKpis,
  TalentBoardEventOpenJob,
  TalentBoardEventOpenTalent,
  TalentBoardEventUpsertComment,
  TalentBoardEventInvitationCreate,
  TalentBoardEventInvitationDelete,
  TalentBoardEventTalentMarkedAsNotSeen,
} from '@bloomays-lib/types.shared';
import { Logger } from '../services/service-logger';
import { monitorException } from '@bloomays-lib/common.monitoring/browser';

const logger = Logger('useTalentBoardEvents');

type GetTalentBoardEventsResponse = {
  getTalentBoardEvents: GqlTalentBoardEvent[];
};

type GetTalentBoardEventKpisResponse = {
  getTalentBoardEventKpis: GqlTalentBoardEventKpis;
};

type CreateTalentBoardEventVariables =
  | Omit<TalentBoardEventOpenJob, 'id' | 'authorEmail'>
  | Omit<TalentBoardEventOpenTalent, 'id' | 'authorEmail'>
  | Omit<TalentBoardEventUpsertComment, 'id' | 'authorEmail'>
  | Omit<TalentBoardEventInvitationCreate, 'id' | 'authorEmail'>
  | Omit<TalentBoardEventInvitationDelete, 'id' | 'authorEmail'>
  | Omit<TalentBoardEventTalentMarkedAsNotSeen, 'id' | 'authorEmail'>;

type UseTalentBoardEventsReturn = {
  events: GqlTalentBoardEvent[];
  kpis: GqlTalentBoardEventKpis | null;
  loading: boolean;
  error: ApolloError | undefined;
  createTalentBoardEvent: (variables: CreateTalentBoardEventVariables) => Promise<boolean>;
  fetchEvents: LazyQueryExecFunction<GetTalentBoardEventsResponse, OperationVariables>;
  fetchKpis: LazyQueryExecFunction<GetTalentBoardEventKpisResponse, OperationVariables>;
};

export const useTalentBoardEvents = (): UseTalentBoardEventsReturn => {
  const [fetchEvents, { data, loading: fetchLoading, error: fetchError }] = useLazyQuery<GetTalentBoardEventsResponse>(
    GET_TALENT_BOARD_EVENTS,
    {
      context: { clientName: 'api.talents' },
      fetchPolicy: 'cache-and-network',
    },
  );

  const [fetchKpis, { data: kpisData, loading: kpisLoading, error: kpisError }] =
    useLazyQuery<GetTalentBoardEventKpisResponse>(GET_TALENT_BOARD_EVENT_KPIS, {
      context: { clientName: 'api.talents' },
      fetchPolicy: 'cache-and-network',
    });

  const [createTalentBoardEventMutation, { error: createError }] = useMutation<
    { createTalentBoardEvent: boolean },
    CreateTalentBoardEventVariables
  >(CREATE_TALENT_BOARD_EVENT, {
    context: { clientName: 'api.talents' },
  });

  const createTalentBoardEvent = useCallback(
    async (variables: CreateTalentBoardEventVariables): Promise<boolean> => {
      try {
        const result = await createTalentBoardEventMutation({ variables });
        return result.data?.createTalentBoardEvent ?? false;
      } catch (err) {
        logger.error('Error creating talent board event', variables, err as Error);
        monitorException(err as Error, { name: 'useTalentBoardEvents.createTalentBoardEvent', variables });
        throw err;
      }
    },
    [createTalentBoardEventMutation],
  );

  const error: ApolloError | undefined = fetchError || createError || kpisError;

  return {
    events: data?.getTalentBoardEvents ?? [],
    kpis: kpisData?.getTalentBoardEventKpis ?? null,
    loading: fetchLoading || kpisLoading,
    error,
    createTalentBoardEvent,
    fetchEvents,
    fetchKpis,
  };
};

export default useTalentBoardEvents;
